import {Route, Router, Routes, useLocation, useNavigate, useParams} from '@startlibs/components'
import React, {useContext, useEffect, useRef, useState} from 'react'
import {PaymentStep} from './steps/PaymentStep'
import {AdminRequestOverview} from './steps/AdminRequestOverview'
import {CASE_ARCHIVED, getDefaultPath} from '../enums/CaseState';
import {ExpertAssignmentStep} from './steps/ExpertAssignmentStep'
import {ExpertReviewStep} from './steps/ExpertReviewStep'
import {FinalReportStep, PrintPdfPreview} from './steps/FinalReportStep'
import {PageContent, PageWrapper, RequestSidebar} from './steps/RequestSidebar'
import {PageLoadingSuspense} from '../components/PageLoading'
import {PatientStep} from './steps/PatientStep'
import {RecordsStep} from './steps/RecordsStep'
import {RejectedCase} from './steps/RejectedCase'
import {getJwt} from '../hooks/useJwt'
import {jwtGetFetcher, jwtPostFetcher} from '../utils/authFetch'
import {willUseSuspense} from '../hooks/useSuspense'
import {ReleaseStep} from './steps/ReleaseStep'
import {ClinicalInformationStep} from './steps/ClinicalInformationStep'
import {ContactPatientStep} from './steps/ContactPatientStep'
import {RemoteConsultationStep} from './steps/RemoteConsultationStep'
import {AgreementsStep} from './steps/AgreementsStep'
import {getTimezoneOffset} from '../utils/utils'
import {CaseNotesStep} from './steps/CaseNotesStep'
import {useToggle} from '@startlibs/core'
import {CaseNotesPanel} from '../components/panel/CaseNotesPanel'
import {Redirect} from "../components/Redirect";
import _ from 'lodash/fp'
import { lazyProviderInfo } from '../components/WithProvider'
import { StatusStep } from './steps/StatusStep';
import { ReconciliationStep } from './steps/ReconciliationStep';
import { ContinuingCareStep } from './steps/ContinuingCareStep';
import { useIsLocale } from '../hocs/IfLocale';
import { DicomRouterContext } from '../utils/DicomRouterContext';
import { DicomsRoutingPanel } from '../components/dicomRoutingPanel/DicomsRoutingPanel';
import styled, {ThemeProvider} from 'styled-components';

const PanelContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  width: 100%;
  height: 0px;
  position: fixed;
  background: #f443368a;
  z-index: 99;
  bottom: 0;
  right: 0;
  align-items: flex-end;
  flex-wrap: nowrap;
  gap: 1rem;
  padding-right: 1rem;
  transition: all 0.5s ease;
`


const useAuthSuspense = willUseSuspense((requestCode) => jwtGetFetcher(getJwt())(`/api/viewCaseRequest?requestCode=${requestCode}&timezoneOffset=${getTimezoneOffset()}`))
const StableThemeProvider = ThemeProvider

export const AdminRequest = () => {

  const params = useParams()
  const requestCode = params[0]
  const navigate = useNavigate()
  const location = useLocation()

  const providerInfo = lazyProviderInfo.read()

  const panelToggle = useToggle()
  const [caseRequest, setCaseRequest] = useState(useAuthSuspense(requestCode))
  const [isLoadingNotes, setLoadingNotes] = useState(false)
  const [caseNotes, setCaseNotes] = useState([])
  const panelFormRef = useRef()

  const isNotLegal = useIsLocale({not:true,contains:"LEGAL"})

  const NotesDeepLinkRedirect = ({requestCode}) => {
  // this useParams will pick up only “noteId”,
  // because our path below defines just that one param
  const { noteId } = useParams()
  return (
    <Redirect
      to={`/admin/request/${requestCode}/notes?id=${noteId}`}
      replace
      noThrow
    />
  )
}

  const { 
    dicomsRoutingPanelToggle
  } = useContext(DicomRouterContext);

  // This will be triggered on close tab/window when panel is open and note active / not saved 
  useEffect(() => {
    const onUnload = function (e) {
      if(!panelToggle.isOpen){
        return;
      }
      if (!panelFormRef.current || !panelFormRef.current.hasChanged) {
        return;
      }
      // Cancel the event
      e.preventDefault();
      // Chrome requires returnValue to be set
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload)
  },[location.search, panelToggle.isOpen, panelFormRef.current])

    // This will be triggered on browser back button when panel is open and note active / not saved 
    useEffect(() => {
      const warningText =
      'You have unsaved changes - are you sure you wish to leave this page?';
      const onPopState = function (e) {
      
        if (!panelFormRef.current || !panelFormRef.current.hasChanged) {
          return;
        }
        
        if(!panelToggle.isOpen){
          return; 
        }

        if(e.target.location.pathname.includes('admin/request') && (
            !e.target.location.pathname.includes('/releases') 
            && !e.target.location.pathname.includes('/records') 
            && !e.target.location.pathname.includes('/remote-consultation')
          )){
          return;
        }
        
        if (window.confirm(warningText)){ return;}
        navigate(location.pathname);
      }
      window.addEventListener('popstate', onPopState);
      return () => window.removeEventListener('popstate', onPopState)
    },[location, panelToggle.isOpen, panelFormRef.current ])

  useEffect(() => {
    if (caseRequest.caseFlags?.adminUpdateReceived) {
      jwtPostFetcher(getJwt())("/api/admin/unsetAdminUpdateReceived?requestId=" + caseRequest.requestId)
    }
  }, [])

  useEffect(() => {
    if (caseRequest.requestId) {
      setLoadingNotes(true);
      jwtGetFetcher(getJwt())("/api/case/notes?requestId=" + caseRequest.requestId)
        .then(response => {setCaseNotes(response); setLoadingNotes(false)})
    }
  }, [])

  if (location.pathname.indexOf("printPdfPreview")>0) {
    return <PrintPdfPreview caseRequest={caseRequest} path="printPdfPreview" />

  }

  return <StableThemeProvider theme={_.set('mobileSidebar',true)}>
    <PageWrapper responsiveWorkaround noSideBar={caseRequest.state === CASE_ARCHIVED}>
      {
        <React.Suspense fallback={null}>
          <RequestSidebar caseRequest={caseRequest} setCaseRequest={setCaseRequest} panelToggle={panelToggle} default isReport={location.pathname.indexOf("report")>0} />
        </React.Suspense>
      }
    <PageContent>
      <PageLoadingSuspense>
        <Router>
        <Routes >
          <Route path={`/admin/request/${requestCode}/case-status`} element={<StatusStep caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>
          {providerInfo?.caseReconciliationEnabled === true && <Route path={`/admin/request/${requestCode}/case-reconciliation`} element={<ReconciliationStep caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>}
          <Route path={`/admin/request/${requestCode}/patient`} element={<PatientStep caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>
          <Route path={`/admin/request/${requestCode}/agreements`} element={<AgreementsStep caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>
          <Route path={`/admin/request/${requestCode}/records`} element={<SuspenseStep Component={RecordsStep} caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>
          <Route path={`/admin/request/${requestCode}/releases`} element={<SuspenseStep Component={ReleaseStep} caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>
          {providerInfo?.managePaymentsEnabled === true && <Route path={`/admin/request/${requestCode}/payment`} element={<PaymentStep caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>}
          {providerInfo?.messagingEnabled === true && <Route path={`/admin/request/${requestCode}/contact`} element={<ContactPatientStep caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>}
          <Route path={`/admin/request/${requestCode}/clinical`} element={<ClinicalInformationStep caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>
          {isNotLegal && <Route path={`/admin/request/${requestCode}/continuing-care`} element={<ContinuingCareStep caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>}
           <Route
              path={`/admin/request/${requestCode}/notes/:noteId`}
              element={<NotesDeepLinkRedirect requestCode={requestCode} />}
            />
          <Route path={`/admin/request/${requestCode}/notes`} 
            element={<SuspenseStep
            Component={CaseNotesStep}
            caseRequest={caseRequest}
            setCaseRequest={setCaseRequest}
            caseNotes={caseNotes} 
            setCaseNotes={setCaseNotes} 
            openPanel={panelToggle}
            isLoadingNotes={isLoadingNotes}
            location={location}
          />}/>
          <Route path={`/admin/request/${requestCode}/expert`} element={<SuspenseStep
            Component={ExpertAssignmentStep}
            caseRequest={caseRequest}
            setCaseRequest={setCaseRequest}
          />}/>
          <Route path={`/admin/request/${requestCode}/rejected`} element={<SuspenseStep
            Component={RejectedCase}
            caseRequest={caseRequest}
            setCaseRequest={setCaseRequest}
          />}/>
          <Route path={`/admin/request/${requestCode}/remote-consultation`} element={<SuspenseStep Component={RemoteConsultationStep} setCaseRequest={setCaseRequest} caseRequest={caseRequest}/>}/>
          <Route path={`/admin/request/${requestCode}/review`} element={<SuspenseStep Component={ExpertReviewStep} caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>
          <Route path={`/admin/request/${requestCode}/report`} element={<SuspenseStep Component={FinalReportStep} caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>
          <Route path={`/admin/request/${requestCode}/upload-report`} element={<SuspenseStep Component={FinalReportStep} uploadPDF caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>
          <Route path={`/admin/request/${requestCode}/overview`} element={<AdminRequestOverview caseRequest={caseRequest} setCaseRequest={setCaseRequest}/>}/>
          <Route path={`/admin/request/${requestCode}*`} element={<Redirect
            to={`/admin/request/${requestCode}/${getDefaultPath(caseRequest, providerInfo)}`}
            default
            replace
            noThrow
          />}/>
          <Route path={`/admin/request/${requestCode}/*`} element={<Redirect
            to={`/admin/request/${requestCode}/${getDefaultPath(caseRequest, providerInfo)}`}
            default
            replace
            noThrow
          />}/>
        </Routes>
        </Router>
      </PageLoadingSuspense>
      <PanelContainer>
        <CaseNotesPanel formRef={panelFormRef} caseRequest={caseRequest} open={panelToggle.isOpen} caseNotes={caseNotes} setCaseNotes={setCaseNotes} close={panelToggle.close} isLoadingNotes={isLoadingNotes}/>
        {dicomsRoutingPanelToggle.isOpen && <DicomsRoutingPanel close={dicomsRoutingPanelToggle.close} />}
      </PanelContainer>
    </PageContent>
  </PageWrapper>
  </StableThemeProvider>
}

const SuspenseStep = ({Component, ...props}) => <PageLoadingSuspense><Component {...props} /></PageLoadingSuspense>
